import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { formatYAxisValue } from '../../Components/DrillDown/formatters';
import GenericModal from '../../Components/GenericModal';
import { EvolucaoVendasDrillDown } from '../../Components/DrillDown';
import { useEvolucaoVendasDrillDown } from '../../Hooks/useGenericDrillDown';

type VendasHistoricoData = {
  periodo: string;
  total_vendas: number;
};

type Props = {
  data: VendasHistoricoData[];
  loading?: boolean;
  title?: string;
  establishmentId?: number | string | null;
  onMissingEstablishment?: () => void;
};

const EvolucaoVendasChart: React.FC<Props> = ({
  data,
  loading,
  title = 'Evolução das Vendas por Mês (Últimos 12 meses)',
  establishmentId,
  onMissingEstablishment
}) => {
  const {
    isModalOpen,
    data: drillDownData,
    loading: drillDownLoading,
    selectedItem: selectedPeriod,
    openModal,
    closeModal
  } = useEvolucaoVendasDrillDown(establishmentId);

  const handleBarClick = async (clickData: any) => {
    if (establishmentId === undefined || establishmentId === null || String(establishmentId).trim() === '') {
      onMissingEstablishment?.();
      return;
    }

    const periodo = clickData.indexValue as string;
    await openModal({ periodo });
  };

  return (
    <>
      <Card sx={{ p: 2}}>
        <CardContent>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#555555' }}>
            {title}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          ) : !data || data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <Typography variant="body1" color="text.secondary">
                📊 Nenhum dado disponível
              </Typography>
            </Box>
          ) : (
            <div style={{ height: 400 }}>
              <ResponsiveBar
                valueFormat={(value) =>
                  new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(value as number)
                }
                data={data}
                keys={['total_vendas']}
                indexBy="periodo"
                margin={{ top: 20, right: 30, bottom: 60, left: 80 }}
                padding={0.3}
                innerPadding={4}
                layout="vertical"
                colors={() => '#3366CC'}
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Vendas R$',
                  legendPosition: 'middle',
                  legendOffset: -70,
                  format: formatYAxisValue
                }}
                labelSkipHeight={12}
                labelTextColor="#fff"
                labelFormat={(value) =>
                  `R$ ${Number(value).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}`
                }
                theme={{
                  labels: {
                    text: {
                      fontSize: 14,
                      fontWeight: 'bold',
                    },
                  },
                }}
                animate={true}
                motionConfig="gentle"
                onClick={handleBarClick}
                tooltip={({ id, value, indexValue }) => (
                  <Box
                    sx={{
                      background: '#fff',

                      p: 2
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {indexValue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(value as number)}
                    </Typography>       
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                      Clique para ver detalhes do período
                    </Typography>
                  </Box>
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <GenericModal
        open={isModalOpen}
        onClose={closeModal}
        title={`Análise Detalhada do Período`}
        loading={drillDownLoading}
        showBreadcrumb={true}
        breadcrumbItems={[{ label: selectedPeriod?.periodo || 'Período' }]}
        maxWidth="xl"
        height="90vh"
      >
        {drillDownData && <EvolucaoVendasDrillDown data={drillDownData} />}
      </GenericModal>
    </>
  );
};

export default EvolucaoVendasChart;
